import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import cx from 'classnames';
import styles from './freeSpinDetails.module.scss';
import { useGetFreeSpinGamesQuery, useGetFreeSpinBetLevelsQuery } from 'store/bonusesApi';
import { ChevronLeftIcon } from 'components/icons';
import { ActionButton } from 'components/actionButton';

interface FreeSpinDetailsProps {
  onBack: () => void;
  onClaim: (gameId: number, betLevel: number) => void;
  isLoadingClaim?: boolean;
  claimAmount?: number;
}

const FreeSpinDetails: React.FC<FreeSpinDetailsProps> = ({
  onBack,
  onClaim,
  isLoadingClaim,
  claimAmount = 0,
}) => {
  const { t } = useTranslation();
  const [selectedGameId, setSelectedGameId] = useState<number | null>(null);
  const [selectedBetLevel, setSelectedBetLevel] = useState<number | null>(null);

  const { data: games, isFetching: isFetchingGames } = useGetFreeSpinGamesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const effectiveGameId = selectedGameId ?? (games && games.length > 0 ? games[0].gameId : null);

  const { data: betLevelsData, isFetching: isFetchingBetLevels } = useGetFreeSpinBetLevelsQuery(
    effectiveGameId as number,
    { skip: !effectiveGameId, refetchOnMountOrArgChange: true },
  );

  const handleGameSelect = (gameId: number) => {
    setSelectedGameId(gameId);
    setSelectedBetLevel(null);
  };

  const betLevels = betLevelsData?.currencies?.[0]?.betLevels || [];

  const selectedBet = betLevels.find((bl: any) => bl.betLevel === selectedBetLevel);
  const spinsCount = selectedBet ? Math.floor(claimAmount / parseFloat(selectedBet.amount)) : 0;

  return (
    <>
      <div
        className={styles.overlay}
        onClick={(e) => {
          e.stopPropagation();
          onBack();
        }}
      />
      <div className={styles.detailsContainer}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={onBack} aria-label={t('rewards.freeSpin.goBack')}>
            <ChevronLeftIcon width="24" height="24" />
          </button>
          <h2 className={styles.title}>{t('rewards.freeSpin.title')}</h2>
        </div>
        <div className={styles.sectionTitle}>{t('rewards.freeSpin.selectGame')}</div>
        {isFetchingGames ? (
          <div className={styles.gamesGrid}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className={cx(styles.gameCard, styles.skeletonCard)} />
            ))}
          </div>
        ) : (
          <div className={styles.gamesGrid}>
            {games?.map((game: any) => (
              <div
                key={game.gameId}
                className={cx(styles.gameCard, {
                  [styles.selected]: effectiveGameId === game.gameId,
                })}
                onClick={() => handleGameSelect(game.gameId)}
              >
                <img src={game.imageUrl} alt={game.title} />
              </div>
            ))}
          </div>
        )}

        {effectiveGameId && (
          <>
            <div className={styles.sectionTitle}>{t('rewards.freeSpin.selectDenomination')}</div>
            {isFetchingBetLevels ? (
              <div className={styles.betLevelsList}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className={cx(styles.betLevelItem, styles.skeletonLevel)} />
                ))}
              </div>
            ) : (
              <div className={styles.betLevelsList}>
                {betLevels.map((bl: any) => (
                  <div
                    key={bl.betLevel}
                    className={cx(styles.betLevelItem, {
                      [styles.selected]: selectedBetLevel === bl.betLevel,
                    })}
                    onClick={(e) => {
                      setSelectedBetLevel(bl.betLevel);
                      const target = e.currentTarget;
                      setTimeout(() => {
                        target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                      }, 50);
                    }}
                  >
                    <div className={styles.levelInfo}>{t('rewards.freeSpin.bet')} </div>
                    <div className={styles.amountInfo}>${bl.amount}</div>
                  </div>
                ))}
              </div>
            )}
            {selectedBetLevel && spinsCount > 0 && (
              <div className={styles.spinsInfo}>
                <Trans
                  i18nKey="rewards.freeSpin.receiveSpins"
                  count={spinsCount}
                  values={{ count: spinsCount }}
                  components={{ strong: <strong /> }}
                />
              </div>
            )}
          </>
        )}

        <div className={styles.claimAction}>
          <ActionButton
            className={styles.claimBtn}
            disabled={!effectiveGameId || !selectedBetLevel}
            isLoading={isLoadingClaim}
            onClick={() => onClaim(effectiveGameId as number, selectedBetLevel as number)}
          >
            {t('rewards.freeSpin.claimBonus')}
          </ActionButton>
        </div>
      </div>
    </>
  );
};

export default FreeSpinDetails;
